import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchFacets } from '@app/src/api/fetchHooks'
import { useCreateResource } from '@app/src/api/updateHooks'
import CreationModalContainer from '@app/src/components/CreationModal/CreationModalContainer'
import CreationModalStep from '@app/src/components/CreationModal/CreationModalStep'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import FileUploadField from '@app/src/components/FileUploadField'
import LoadingButton from '@app/src/components/LoadingButton'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import CreationModalProgressContext from '@app/src/context/CreationModalProgressContext'
import type RequestError from '@app/src/errors/RequestError'
import useCurrentProviderType from '@app/src/hooks/currentProviderType'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { useOwnerRestriction } from '@app/src/hooks/useOwnerRestriction'
import { CONFIGURATION_PAGE_IDS } from '@app/src/pages/Configurations/ConfigurationsScene'
import sentryService from '@app/src/services/SentryService'
import { ImportTemplates } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { FileDownloadOutlined } from '@mui/icons-material'
import { Alert, Box, Button, Dialog, DialogContent, Divider, LinearProgress, Stack, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useQueryClient } from '@tanstack/react-query'
import { useConfirm } from 'material-ui-confirm'
import React, { useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router-dom'
import { Operators } from '../../../Filters/useFilters'
import EnrichmentInfoStep from '../ScheduleEnrichment/EnrichmentInfoStep'
import type { ScheduleEnrichmentFormData } from '../ScheduleEnrichment/ScheduleEnrichmentModal'
import { SCHEDULE_ENRICHMENT_QUESTION_KEYS } from '../ScheduleEnrichment/ScheduleEnrichmentModal'
import { useScheduleEnrichment } from '../ScheduleEnrichment/hooks/scheduleEnrichment'
import ImportDoneView from './ImportDoneView'
import ImportEnrichingView from './ImportEnrichingView'
import ImportSummaryStep from './ImportSummaryStep'

type ImportProvidersModalProps = {
  open: boolean
  onClose: () => void
}

export type UploadImportFileResponse = {
  successCount: number
  errorCount: number
  onSchedulingAiScreeningError: boolean
  aiScreeningScheduledCount: number
  importedProviderIds: number[]
}

const useStyles = makeStyles(({ spacing }) => ({
  infoAlert: {
    marginTop: spacing(2),
  },
}))

enum ImportFlowStep {
  Summary = 1,
  Enrichment = 2,
}

const TOTAL_POST_IMPORT_STEPS = 3

const ImportProvidersModal: React.FC<ImportProvidersModalProps> = ({ open, onClose }) => {
  const { formatMessage } = useIntl()
  const resourceType = useCurrentProviderType()
  const { solution } = useAuthentication().scope
  const { showErrorNotification } = useErrorNotification()
  const classes = useStyles()
  const { closeDrawer } = useDrawer()
  const queryClient = useQueryClient()
  const { mutate: uploadImportFile, isPending: isLoading } = useCreateResource<UploadImportFileResponse, FormData>()
  const [importResult, setImportResult] = useState<UploadImportFileResponse | undefined>()
  const [importFile, setImportFile] = useState<string | Blob>('')
  const history = useHistory()
  const confirm = useConfirm()
  const { accountIsOwnerRestricted } = useOwnerRestriction()
  const { scheduleEnrichment, isScheduling } = useScheduleEnrichment()

  const [postImportStep, setPostImportStep] = useState<ImportFlowStep>(ImportFlowStep.Summary)
  const [showFinalView, setShowFinalView] = useState(false)
  const [enrichmentScheduled, setEnrichmentScheduled] = useState(false)
  const [enrichmentEnabled, setEnrichmentEnabled] = useState(true)

  // FormProvider is required by CreationModalStep and EnrichmentInfoStep
  const formMethods = useForm<ScheduleEnrichmentFormData>({
    defaultValues: {
      [SCHEDULE_ENRICHMENT_QUESTION_KEYS.PROVIDER_IDS]: [],
      [SCHEDULE_ENRICHMENT_QUESTION_KEYS.SEND_EMAIL_ON_COMPLETE]: false,
    },
  })

  const {
    facets: [automations],
    isLoading: isLoadingAutomations,
  } = useFetchFacets({
    key: [FetchKey.AutomationFacets],
    endpoint: endpoints.automationsWithFacets,
    options: { enabled: !!importFile },
    facetsParam: [{ name: 'automationType' }],
    filter: [
      {
        name: 'automationType',
        filters: [
          { value: 'AiScreen', operator: Operators.EqualTo },
          { value: 'AiEnrich', operator: Operators.EqualTo },
        ],
      },
    ],
  })

  const hasAiScreenAutomations = automations?.some(x => x.value === 'AiScreen' && x.count > 0)
  const hasAiEnrichAutomations = automations?.some(x => x.value === 'AiEnrich' && x.count > 0)

  const handleTemplateDownload = (): Window | null => window.open(ImportTemplates[resourceType], '_blank')

  const onImport = () => {
    if (hasAiScreenAutomations) {
      confirm({
        title: formatMessage({ id: 'import.importWithAiScreenAutomation.title' }),
        description: formatMessage({ id: 'import.importWithAiScreenAutomation.description' }, { br: <br /> }),
        confirmationText: formatMessage({ id: 'import.continueToImport' }),
        cancellationText: formatMessage({ id: 'import.viewAutomation' }),
        cancellationButtonProps: { variant: 'text' },
      })
        .then(() => handleImport())
        .catch(error => {
          if (error && typeof error === 'object' && error.message && !error.message.includes('cancel')) {
            sentryService.captureError(error as Error, {
              tags: {
                errorType: 'dialog-system',
                operation: 'import-confirmation',
                component: 'import-providers-modal',
              },
            })
          }
          handleCancelImport()
        })
      return
    }
    handleImport()
  }

  const handleImport = async (): Promise<void> => {
    const data = new FormData()
    data.append('file', importFile)

    uploadImportFile(
      { url: endpoints.importData(solution, resourceType), body: data },
      {
        onSuccess: ({
          successCount = 0,
          errorCount = 0,
          onSchedulingAiScreeningError = false,
          aiScreeningScheduledCount = 0,
          importedProviderIds = [],
        }) => {
          setImportResult({
            successCount,
            errorCount,
            onSchedulingAiScreeningError,
            aiScreeningScheduledCount,
            importedProviderIds,
          })
          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderCollection] })

          if (hasAiEnrichAutomations && successCount > 0) {
            queryClient.invalidateQueries({ queryKey: [FetchKey.JobRunning, 'enrichment'] })
          }

          if (hasAiScreenAutomations && successCount > 0) {
            queryClient.invalidateQueries({ queryKey: [FetchKey.JobRunning, 'screening'] })
          }

          closeDrawer()
        },
        onError: e => {
          showErrorNotification({ requestError: e as RequestError })
        },
      },
    )
  }

  const handleCloseImportDialog = (): void => {
    setImportResult(undefined)
    setImportFile('')
    setPostImportStep(ImportFlowStep.Summary)
    setShowFinalView(false)
    setEnrichmentScheduled(false)
    setEnrichmentEnabled(true)
    onClose()
  }

  const handleCancelImport = (): void => {
    handleCloseImportDialog()
    history.push(
      generatePath(paths.configurations, {
        configurationsPage: CONFIGURATION_PAGE_IDS.Automations,
      }),
    )
  }

  const handleEnrichmentSubmit = async () => {
    // Skip manual enrichment scheduling if AI Enrich automation exists (it will run automatically)
    if (!hasAiEnrichAutomations && enrichmentEnabled && importResult && importResult.importedProviderIds.length > 0) {
      const sendEmailOnComplete = formMethods.getValues(SCHEDULE_ENRICHMENT_QUESTION_KEYS.SEND_EMAIL_ON_COMPLETE)
      await scheduleEnrichment({
        providerIds: importResult.importedProviderIds,
        sendEmailOnComplete,
      })
      setEnrichmentScheduled(true)
    }
    setShowFinalView(true)
  }

  // Auto-select email checkbox when enrichment count >= 20
  React.useEffect(() => {
    if (postImportStep === ImportFlowStep.Enrichment && importResult && importResult.successCount >= 20) {
      formMethods.setValue(SCHEDULE_ENRICHMENT_QUESTION_KEYS.SEND_EMAIL_ON_COMPLETE, true)
    }
  }, [postImportStep, importResult, formMethods])

  // If AI Enrich automation exists, skip the enrichment step
  const shouldSkipEnrichmentStep = hasAiEnrichAutomations

  const progress = useMemo(() => {
    return Math.round((postImportStep / TOTAL_POST_IMPORT_STEPS) * 100)
  }, [postImportStep])

  const noopSetNumber = useMemo(() => () => undefined, [])
  const noopSetBoolean = useMemo(() => () => undefined, [])

  const progressContextValue = useMemo(
    () => ({
      activeStep: postImportStep,
      totalSteps: TOTAL_POST_IMPORT_STEPS,
      progress,
      setTotalSteps: noopSetNumber,
      setActiveStep: setPostImportStep as React.Dispatch<React.SetStateAction<number>>,
      allowsNextWithoutData: true,
      setAllowsNextWithoutData: noopSetBoolean,
      enableEmptyState: false,
      setEnableEmptyState: noopSetBoolean,
    }),
    [postImportStep, progress, noopSetNumber, noopSetBoolean],
  )

  const hasSuccessfulImports = importResult && importResult.successCount > 0
  const isResultNoNewEntries = importResult && importResult.successCount === 0 && importResult.errorCount === 0
  const isAllErrors = importResult && importResult.successCount === 0 && importResult.errorCount > 0
  const canContinueFromSummary = hasSuccessfulImports

  const getStepTitle = () => {
    if (postImportStep === ImportFlowStep.Summary) {
      return formatMessage({ id: 'import.summary.title' })
    }
    return formatMessage({ id: 'import.enrichment.title' })
  }

  const renderFinalViewHeader = () => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" px={4} pt={2} mb={2}>
        <Typography variant="subtitle1">
          {formatMessage({ id: 'import.provider' }, { providerType: resourceType })}
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={100} />
    </>
  )

  const renderPostImportFlow = () => {
    if (!importResult) return null

    if (showFinalView) {
      return (
        <DialogContent sx={{ padding: '0px 0px' }}>
          {renderFinalViewHeader()}
          {enrichmentScheduled ? (
            <ImportEnrichingView errorCount={importResult.errorCount} onClose={handleCloseImportDialog} />
          ) : (
            <ImportDoneView
              successCount={importResult.successCount}
              errorCount={importResult.errorCount}
              onClose={handleCloseImportDialog}
            />
          )}
        </DialogContent>
      )
    }

    if (isAllErrors) {
      return (
        <DialogContent sx={{ padding: '0px 0px' }}>
          {renderFinalViewHeader()}
          <ImportDoneView
            successCount={importResult.successCount}
            errorCount={importResult.errorCount}
            onClose={handleCloseImportDialog}
          />
        </DialogContent>
      )
    }

    if (isResultNoNewEntries || !hasSuccessfulImports) {
      return (
        <DialogContent sx={{ padding: '0px 0px' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" px={4} pt={2} mb={2}>
            <Typography variant="subtitle1">{formatMessage({ id: 'import.summary.noNewEntriesTitle' })}</Typography>
            <Button variant="text" onClick={handleCloseImportDialog}>
              {formatMessage({ id: 'general.close' })}
            </Button>
          </Box>
          <Divider />
          <Stack flexGrow={1} alignItems="center">
            <Box display="flex" flexDirection="column" justifyContent="center" width={680} py={6}>
              <Typography variant="h1" gutterBottom>
                {formatMessage({ id: 'import.summary.noNewEntriesTitle' })}
              </Typography>
              <Typography color="textSecondary">{formatMessage({ id: 'import.summary.noNewEntriesInfo' })}</Typography>
              <Box display="flex" justifyContent="flex-end" pt={4}>
                <Button variant="contained" onClick={handleCloseImportDialog}>
                  {formatMessage({ id: 'general.close' })}
                </Button>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
      )
    }

    return (
      <CreationModalProgressContext.Provider value={progressContextValue}>
        <CreationModalContainer title={getStepTitle()} onClose={handleCloseImportDialog} showProgress>
          <FormProvider {...formMethods}>
            <Stack flexGrow={1} alignItems="center">
              {postImportStep === ImportFlowStep.Summary && (
                <CreationModalStep
                  title={formatMessage({ id: 'import.summary.title' })}
                  description={formatMessage({ id: 'import.summary.stepDescription' })}
                  fieldnames={[]}
                  disableInitialBack
                  overrideButtonTextId="general.continue"
                  widthOverride={584}
                  canGoNext={() => Boolean(canContinueFromSummary)}
                  customOnClickNext={
                    shouldSkipEnrichmentStep
                      ? async () => {
                          setShowFinalView(true)
                        }
                      : undefined
                  }
                >
                  <ImportSummaryStep
                    successCount={importResult.successCount}
                    errorCount={importResult.errorCount}
                    hasAiEnrichAutomation={hasAiEnrichAutomations}
                  />
                </CreationModalStep>
              )}

              {postImportStep === ImportFlowStep.Enrichment && !shouldSkipEnrichmentStep && (
                <CreationModalStep
                  title={formatMessage({ id: 'import.enrichment.title' })}
                  description={formatMessage({ id: 'import.enrichment.description' })}
                  fieldnames={[]}
                  widthOverride={584}
                  inlineButtons
                >
                  <EnrichmentInfoStep
                    selectedCount={importResult.successCount}
                    isEnrichmentEnabled={enrichmentEnabled}
                    onEnrichmentToggle={setEnrichmentEnabled}
                    isLoading={isScheduling}
                    onSubmit={handleEnrichmentSubmit}
                  />
                </CreationModalStep>
              )}
            </Stack>
          </FormProvider>
        </CreationModalContainer>
      </CreationModalProgressContext.Provider>
    )
  }

  return (
    <Dialog open={open} onClose={handleCloseImportDialog} fullScreen closeAfterTransition>
      {importResult ? (
        renderPostImportFlow()
      ) : (
        <DialogContent sx={{ padding: '0px 0px' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" px={4} pt={2} mb={2}>
            <Box display="flex" alignItems="center">
              <Typography variant="subtitle1">
                {formatMessage({ id: 'import.provider' }, { providerType: resourceType })}
              </Typography>
            </Box>

            <Button variant="text" onClick={handleCloseImportDialog}>
              {formatMessage({ id: 'general.cancel' })}
            </Button>
          </Box>

          <LinearProgress variant="determinate" value={10} />

          <Stack flexGrow={1} alignItems="center">
            <Box display="flex" flexDirection="column" justifyContent="center" width={680} py={6}>
              <Stack spacing={2} pb={4}>
                <Typography variant="h1" gutterBottom>
                  {formatMessage({ id: 'import.provider' }, { providerType: resourceType })}
                </Typography>
              </Stack>
              <Stack p={2} spacing={5}>
                {accountIsOwnerRestricted && (
                  <Alert severity="info" className={classes.infoAlert}>
                    {formatMessage({ id: 'import.importFromOwnerRestrictedAccount' }, { providerType: resourceType })}
                  </Alert>
                )}
                <Stack>
                  <Typography variant="subtitle1">{formatMessage({ id: 'import.stepOne' })}</Typography>
                  <Typography gutterBottom variant="body1" color="textSecondary" component="span">
                    {formatMessage({ id: 'import.stepOneDescription' })}
                  </Typography>
                  <Box mt={2}>
                    <Button
                      startIcon={<FileDownloadOutlined />}
                      variant="outlined"
                      onClick={handleTemplateDownload}
                      disabled={isLoading}
                    >
                      {formatMessage({ id: 'import.templateDownload' })}
                    </Button>
                  </Box>
                </Stack>
                <Divider sx={{ my: 4 }} />
                <Stack>
                  <Typography variant="subtitle1">{formatMessage({ id: 'import.stepTwo' })}</Typography>
                  <Typography gutterBottom variant="body1" color="textSecondary" component="span">
                    {formatMessage({ id: 'import.stepTwoDescription' })}
                  </Typography>
                  <Box mt={2}>
                    <Stack width={488}>
                      <Alert
                        variant="filled"
                        severity="info"
                        sx={{ backgroundColor: 'common.white', alignItems: 'flex-start' }}
                      >
                        {formatMessage({ id: 'import.stepTwoInfo' })}
                      </Alert>
                    </Stack>
                  </Box>
                </Stack>
                <Divider sx={{ my: 4 }} />
                <Stack>
                  <Typography variant="subtitle1">{formatMessage({ id: 'import.stepThree' })}</Typography>
                  <Typography color="textSecondary" variant="body1">
                    {formatMessage({ id: 'import.stepThreeDescription' })}
                  </Typography>
                  <Box mt={2}>
                    <FileUploadField setFile={setImportFile} />
                  </Box>
                </Stack>
                <Box display="flex" justifyContent="flex-end" pt={4}>
                  <LoadingButton
                    loading={isLoading}
                    variant="contained"
                    onClick={onImport}
                    disabled={!importFile || isLoadingAutomations}
                  >
                    {formatMessage({ id: 'import.import' })}
                  </LoadingButton>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
      )}
    </Dialog>
  )
}

export default ImportProvidersModal
